/**
 * The numbers that describe a whole show at a glance, for the readout in the
 * corner of the canvas. Pure, so `npm run test:unit` covers it.
 *
 * Every figure here follows the catalogue's rule: a device either has a
 * manufacturer-published draw or it has none, and a device with none enters no
 * total — it is counted and the readout says so. The power maths reuses the
 * load-schedule chain (`resolveDraw` → `wallWatts`) so the corner number and
 * the power page never disagree about the same device.
 *
 * What it deliberately does NOT do: attribute a PoE camera's watts onto its
 * switch. The plan does that because it knows which switch; the canvas only
 * knows there is a cable. Those devices are counted under "fed by another
 * device" instead, which is true and never double-counts.
 */

import { buildPackList, type CableRun } from './cables'
import { resolveDraw, wallWatts, type PowerSource } from './power'

export type ShowSummaryDevice = {
  id: string
  powerDrawTypicalW: number | null
  powerDrawMaxW: number | null
  powerSource: PowerSource
  formFactorRU: number
}

export type ShowSummary = {
  power: {
    /** Peak watts at the wall for every device with a published figure. */
    wallMaxW: number
    /** Typical watts at the wall; falls back to max where only max is known. */
    wallTypicalW: number
    /** Mains- or adapter-fed devices whose figure entered the total. */
    counted: number
    /** Mains- or adapter-fed devices with no published figure. */
    unknown: number
    /** PoE, PoC, bus- or battery-powered, or drawing nothing: not a wall load here. */
    fedElsewhere: number
  }
  cables: {
    count: number
    /** Metres across every run that has a length. */
    totalMetres: number
    /** Runs with no length yet. */
    unmeasured: number
    /** Runs longer than the cable reliably carries at this format. */
    overReach: number
  }
  devices: {
    count: number
    /** Placed but with no cable on any port. */
    unconnected: number
    /** Rack units across every rack-mount device. */
    rackUnits: number
  }
}

/** Sources whose published watts are drawn from the wall by THIS device. */
const WALL_SOURCES: ReadonlySet<PowerSource> = new Set(['MAINS_AC', 'DC_ADAPTER', 'UNKNOWN'])

export function summariseShow(
  placed: readonly { nodeId: string; device: ShowSummaryDevice }[],
  runs: readonly CableRun[],
  /** nodeId of each end of every run, for the unconnected count. */
  runEnds: readonly { source: string; target: string }[],
  showFormat: string | null,
): ShowSummary {
  let wallMaxW = 0
  let wallTypicalW = 0
  let counted = 0
  let unknown = 0
  let fedElsewhere = 0
  let rackUnits = 0

  for (const { device } of placed) {
    rackUnits += device.formFactorRU > 0 ? device.formFactorRU : 0
    if (!WALL_SOURCES.has(device.powerSource)) {
      fedElsewhere += 1
      continue
    }
    const { typicalW, maxW } = resolveDraw(device)
    if (maxW == null) {
      unknown += 1
      continue
    }
    counted += 1
    wallMaxW += wallWatts(maxW, device.powerSource)
    wallTypicalW += wallWatts(typicalW ?? maxW, device.powerSource)
  }

  const connected = new Set<string>()
  for (const end of runEnds) {
    connected.add(end.source)
    connected.add(end.target)
  }
  const unconnected = placed.filter((entry) => !connected.has(entry.nodeId)).length

  const pack = buildPackList([...runs], { kind: 'NONE' }, showFormat)
  const overReach = pack.warnings.reduce((sum, warning) => sum + warning.count, 0)

  return {
    power: { wallMaxW, wallTypicalW, counted, unknown, fedElsewhere },
    cables: {
      count: runs.length,
      totalMetres: pack.totalMetres,
      unmeasured: pack.unmeasured.length,
      overReach,
    },
    devices: { count: placed.length, unconnected, rackUnits },
  }
}
